import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { Observable, map, switchMap, forkJoin, of } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class ApolloUserService {

    constructor(private apollo: Apollo) { }

    loginUser(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const LOGIN_USER = gql`
            mutation {
                user {
                    authenticate(${inputString}) {
                        id,
                        email,
                        firstName,
                        lastName,
                        phoneNumber,
                        token,
                        error
                    }
                }
            }   
        `;

        return this.apollo.mutate<any>({
            mutation: LOGIN_USER
        }).pipe(
            map(result => {
                const user = result?.data?.user?.authenticate;
                return user;
            })
        );
    }

    requestPasswordReset(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const REQUEST_PASSWORD_RESET = gql`
            mutation {
                user {
                    requestPasswordReset(input: { ${inputString} }) {
                        success
                        error
                    }
                }
            }
        `;

        return this.apollo.mutate<any>({
            mutation: REQUEST_PASSWORD_RESET
        }).pipe(
            map(result => result?.data?.user?.requestPasswordReset)
        );
    }

    resetPasswordWithToken(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const RESET_PASSWORD_WITH_TOKEN = gql`
            mutation {
                user {
                    resetPasswordWithToken(input: { ${inputString} }) {
                        success
                        error
                    }
                }
            }
        `;

        return this.apollo.mutate<any>({
            mutation: RESET_PASSWORD_WITH_TOKEN
        }).pipe(
            map(result => result?.data?.user?.resetPasswordWithToken)
        );
    }


    getUserList(refRole?: string): Observable<any[]> {
        const GET_USERLIST = gql`
            query {
                user {
                    allUsers(pageNo:1, refRole: ${refRole ? '"' + refRole + '"' : null}) {
                        count
                        pageNo
                        pageSize
                        totalPages
                        data {
                            id
                            email
                            firstName
                            lastName
                            phoneNumber
                            isActive
                            userRoles {
                                id
                                refRole {
                                    id
                                    name
                                }
                                school {
                                    id
                                    name
                                }
                            }
                        }
                    }
                }
            }
        `;

        return this.apollo.watchQuery<any>({
            query: GET_USERLIST,
            fetchPolicy: 'network-only',
        }).valueChanges.pipe(
            map(result => {
                const userListData = result?.data?.user?.allUsers?.data || [];
                return userListData;
            })
        );
    }


    getCheckInOutList(startDate: string, endDate: string): Observable<any[]> {
        const GET_STUDENTLIST = gql`
            query {
                staffCheckInCheckOut {
                    allStaffCheckInCheckOut(pageNo: 1, startDate: "${startDate}", endDate: "${endDate}") {
                        count
                        pageNo
                        pageSize
                        totalPages
                        data {
                            id
                            checkInDateTime
                            checkOutDateTime
                            userRole {
                                id
                                user {
                                    id
                                    email
                                    firstName
                                    lastName
                                    phoneNumber
                                }
                                refRole {
                                    id
                                    name
                                }
                            }
                            class {
                                id
                                name
                                grade {
                                    id
                                    name
                                    isStaffSpecificStudent
                                }
                                schoolYear {
                                    year
                                    month
                                }
                            }
                            school {
                                id
                                name
                                description
                                address
                                postalCode
                                city
                                state {
                                    id
                                    name
                                }
                                weekend {
                                    id
                                    type
                                }
                            }
                        }
                    }
                }
            }
        `;

        return this.apollo.watchQuery<any>({
            query: GET_STUDENTLIST
        }).valueChanges.pipe(
            map(result => {
                const studentListData = result?.data?.staffCheckInCheckOut?.allStaffCheckInCheckOut?.data || [];
                return studentListData;
            })
        );
    }

    private getCheckInOutListPage(startDate: string, endDate: string, pageNo: number): Observable<any> {
        const GET_STUDENTLIST = gql`
            query {
                staffCheckInCheckOut {
                    allStaffCheckInCheckOut(pageNo: ${pageNo}, startDate: "${startDate}", endDate: "${endDate}") {
                        count
                        pageNo
                        pageSize
                        totalPages
                        data {
                            id
                            checkInDateTime
                            checkOutDateTime
                            userRole {
                                id
                                user {
                                    id
                                    email
                                    firstName
                                    lastName
                                    phoneNumber
                                }
                                refRole {
                                    id
                                    name
                                }
                            }
                            class {
                                id
                                name
                                grade {
                                    id
                                    name
                                    isStaffSpecificStudent
                                }
                                schoolYear {
                                    year
                                    month
                                }
                            }
                            school {
                                id
                                name
                                description
                                address
                                postalCode
                                city
                                state {
                                    id
                                    name
                                }
                                weekend {
                                    id
                                    type
                                }
                            }
                        }
                    }
                }
            }
        `;

        return this.apollo.query<any>({
            query: GET_STUDENTLIST,
            fetchPolicy: 'network-only',
        }).pipe(
            map(result => {
                const response = result?.data?.staffCheckInCheckOut?.allStaffCheckInCheckOut;
                return {
                    data: response?.data || [],
                    totalPages: response?.totalPages || 1,
                    pageNo: response?.pageNo || 1,
                    count: response?.count || 0
                };
            })
        );
    }

    getAllCheckInOutList(startDate: string, endDate: string): Observable<any[]> {
        return this.getCheckInOutListPage(startDate, endDate, 1).pipe(
            switchMap((firstPage: any) => {
                console.log('[ApolloUserService] getAllCheckInOutList - First page:', {
                    totalPages: firstPage.totalPages,
                    pageNo: firstPage.pageNo,
                    count: firstPage.count,
                    dataCount: firstPage.data?.length || 0
                });
                
                const totalPages = firstPage.totalPages || 1;
                if (totalPages <= 1) {
                    return of(firstPage.data || []);
                }
                
                // Fetch all remaining pages
                const pageObservables: Observable<any>[] = [of(firstPage.data || [])];
                for (let page = 2; page <= totalPages; page++) {
                    pageObservables.push(
                        this.getCheckInOutListPage(startDate, endDate, page).pipe(
                            map((pageResult: any) => {
                                console.log(`[ApolloUserService] getAllCheckInOutList - Page ${page}:`, {
                                    dataCount: pageResult.data?.length || 0
                                });
                                return pageResult.data || [];
                            })
                        )
                    );
                }
                
                return forkJoin(pageObservables).pipe(
                    map((pages: any[][]) => {
                        const allData = pages.flat();
                        console.log('[ApolloUserService] getAllCheckInOutList - All pages combined:', {
                            totalRecords: allData.length
                        });
                        return allData;
                    })
                );
            })
        );
    }

    getStaffNotCheckInList(startDate: string, endDate: string): Observable<any[]> {
        const GET_STUDENTLIST = gql`
            query {
                staffCheckInCheckOut {
                    allStaffNotCheckedIn(pageNo: 1, startDate: "${startDate}", endDate: "${endDate}") {
                        count
                        pageNo
                        pageSize
                        totalPages
                        data {
                            id
                            email
                            firstName
                            lastName
                            phoneNumber
                            userRoles {
                                id
                                refRole {
                                    id
                                    name
                                }
                                school {
                                    id
                                    name
                                    description
                                    address
                                    postalCode
                                    city
                                    state {
                                        id
                                        name
                                    }
                                    weekend {
                                        id
                                        type
                                    }
                                }
                            }
                        }
                    }
                }
            }
        `;

        return this.apollo.watchQuery<any>({
            query: GET_STUDENTLIST
        }).valueChanges.pipe(
            map(result => {
                const studentListData = result?.data?.staffCheckInCheckOut?.allStaffNotCheckedIn?.data || [];
                return studentListData;
            })
        );
    }

    getStaffAttendanceSummary(startDate: string, endDate: string): Observable<{ present: number; absent: number }> {
        const GET_ATTENDANCE = gql`
            query {
                staffCheckInCheckOut {
                    allStaffCheckInCheckOut(pageNo: 1, startDate: "${startDate}", endDate: "${endDate}") {
                        count
                    }
                    allStaffNotCheckedIn(pageNo: 1, startDate: "${startDate}", endDate: "${endDate}") {
                        count
                    }
                }
            }
        `;

        return this.apollo.query<any>({
            query: GET_ATTENDANCE
        }).pipe(
            map(result => {
                const root = result?.data?.staffCheckInCheckOut;
                return {
                    present: Number(root?.allStaffCheckInCheckOut?.count ?? 0),
                    absent: Number(root?.allStaffNotCheckedIn?.count ?? 0)
                };
            })
        );
    }

    getUserCountByRole(roleName: string): Observable<number> {
        const GET_COUNT = gql`
            query {
                user {
                    allUsers(pageNo: 1, refRole: "${roleName}") {
                        count
                    }
                }
            }
        `;

        return this.apollo.query<any>({
            query: GET_COUNT
        }).pipe(
            map(result => Number(result?.data?.user?.allUsers?.count ?? 0))
        );
    }

    getAllStaffAssignee(startDate: string, endDate: string): Observable<any[]> {
        const GET_STUDENTLIST = gql`
            query GetAllStaffAssignee($startDate: Date, $endDate: Date) {
                staffAssignee {
                    allStaffAssignee(pageNo: 1, startDate: $startDate, endDate: $endDate) {
                        count
                        pageNo
                        pageSize
                        totalPages
                        data {
                            id
                            assignDateTime
                            schoolId
                            school {
                                id
                                name
                                description
                                address
                                postalCode
                                city
                                state {
                                    id
                                    name
                                }
                                weekend {
                                    id
                                    type
                                }
                            }
                            studentId
                            student {
                                id
                                firstName
                                lastName
                                gender
                                birthCertificateNo
                                myKidNo
                                dateOfBirth
                                isOku
                                isActive
                                refOkuType {
                                    name
                                    description
                                }
                                class {
                                    id
                                    name
                                    grade {
                                        id
                                        name
                                        isStaffSpecificStudent
                                    }
                                }
                            }
                            userRoleId
                            userRole {
                                id
                                refRole {
                                    id
                                    name
                                }
                                school {
                                    id
                                    name
                                    description
                                    address
                                    postalCode
                                    city
                                    state {
                                        id
                                        name
                                    }
                                    weekend {
                                        id
                                        type
                                    }
                                }
                                user {
                                    email
                                    firstName
                                    lastName
                                    phoneNumber
                                }
                            }
                        }
                    }
                }
            }
        `;
    
        return this.apollo.watchQuery<any>({
            query: GET_STUDENTLIST,
            variables: {
                startDate,
                endDate
            }
        }).valueChanges.pipe(
            map(result => {
                const studentListData = result?.data?.staffAssignee?.allStaffAssignee?.data || [];
                return studentListData;
            })
        );
    }

    getUserRoleList(): Observable<any[]> {
        const GET_USERLIST = gql`
            query {
                user {
                    allUserRoles {
                        createdBy
                        createdDate
                        id
                        isActive
                        isDeleted
                        refRoleId
                        schoolId
                        updatedBy
                        updatedDate
                        userId
                        refRole {
                            createdBy
                            createdDate
                            id
                            isActive
                            isDeleted
                            name
                            updatedBy
                            updatedDate
                        }
                    }
                }
            }
        `;

        return this.apollo.watchQuery<any>({
            query: GET_USERLIST
        }).valueChanges.pipe(
            map(result => {
                const userListData = result?.data?.user?.allUserRoles?.data || [];
                return userListData;
            })
        );
    }


    getMasterStaffAssigneeList(userRoleId: number): Observable<any[]> {
        const GET_STUDENTLIST = gql`
            query {
                masterStaffAssignee {
                    allMasterStaffAssignees(pageNo: 1, userRoleId: ${userRoleId}) {
                        count
                        pageNo
                        pageSize
                        totalPages
                        data {
                            id
                            student {
                                id
                                firstName
                                lastName
                                gender
                                birthCertificateNo
                                myKidNo
                                dateOfBirth
                                isOku
                                isActive
                                refOkuType {
                                    name
                                    description
                                }
                                class {
                                    name
                                    grade {
                                    name
                                    isStaffSpecificStudent
                                    }
                                }
                                schoolYear {
                                    year
                                    month
                                    school {
                                        id
                                        name
                                        description
                                        address
                                        postalCode
                                        city
                                        state {
                                            id
                                            name
                                        }
                                        weekend {
                                            id
                                            type
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        `;

        return this.apollo.watchQuery<any>({
            query: GET_STUDENTLIST,
            fetchPolicy: 'network-only'
        }).valueChanges.pipe(
            map(result => {
                const studentListData = result?.data?.masterStaffAssignee?.allMasterStaffAssignees?.data || [];
                return studentListData;
            })
        );
    }


    getTempStaffAssigneeList(userRoleId: number, assignDate?: string): Observable<any[]> {
        const GET_STUDENTLIST = gql`
            query {
                tempStaffAssignee {
                    allTempStaffAssignee(pageNo: 1, userRoleId: ${userRoleId}, assignDate: ${assignDate ? '"' + assignDate + '"' : null}) {
                        count
                        pageNo
                        pageSize
                        totalPages
                        data {
                            id
                            assignDateTime
                            student {
                                id
                                firstName
                                lastName
                                gender
                                birthCertificateNo
                                myKidNo
                                dateOfBirth
                                isOku
                                isActive
                                refOkuType {
                                    name
                                    description
                                }
                                class {
                                    name
                                    grade {
                                        name
                                        isStaffSpecificStudent
                                    }
                                }
                                schoolYear {
                                    year
                                    month
                                    school {
                                        id
                                        name
                                        description
                                        address
                                        postalCode
                                        city
                                        state {
                                            id
                                            name
                                        }
                                        weekend {
                                            id
                                            type
                                        }
                                    }
                                }
                            }
                            userRoleFrom {
                                id
                                refRole {
                                    name
                                }
                                user {
                                    email
                                    firstName
                                    lastName
                                    phoneNumber
                                }
                            }
                            userRoleTo {
                                id
                                refRole {
                                    name
                                }
                                user {
                                    email
                                    firstName
                                    lastName
                                    phoneNumber
                                }
                            }
                        }
                    }
                }
            }
        `;

        return this.apollo.watchQuery<any>({
            query: GET_STUDENTLIST,
            fetchPolicy: 'network-only'
        }).valueChanges.pipe(
            map(result => {
                const studentListData = result?.data?.tempStaffAssignee?.allTempStaffAssignee?.data || [];
                return studentListData;
            })
        );
    }


    getStaffAssigneeList(startDate: string, endDate: string, staffUserRoleId?: number): Observable<any[]> {
        const GET_STUDENTLIST = gql`
            query {
                staffAssignee {
                    allStaffAssignee(pageNo: 1, startDate: "${startDate}", endDate: "${endDate}", staffUserRoleId: ${staffUserRoleId ? staffUserRoleId : null}) {
                        count
                        pageNo
                        pageSize
                        totalPages
                        data {
                            id
                            assignDateTime
                            userRole {
                                id
                                refRole {
                                    name
                                }
                                user {
                                    email
                                    firstName
                                    lastName
                                    phoneNumber
                                }
                            }
                            student {
                                id
                                firstName
                                lastName
                                gender
                                birthCertificateNo
                                myKidNo
                                dateOfBirth
                                isOku
                                isActive
                                refOkuType {
                                    name
                                    description
                                }
                                class {
                                    name
                                    grade {
                                        name
                                        isStaffSpecificStudent
                                        capping
                                    }
                                }
                                schoolYear {
                                    year
                                    month
                                    school {
                                        id
                                        name
                                        description
                                        address
                                        postalCode
                                        city
                                        state {
                                            id
                                            name
                                        }
                                        weekend {
                                            id
                                            type
                                        }
                                    }
                                }
                            }
                            studentCheckInCheckOut {
                                id
                                checkInDateTime
                                checkInRemarks
                                checkOutDateTime
                                checkOutRemarks
                                temperature
                                isSick
                                refSickType {
                                    id
                                    name
                                    description
                                }
                                studentCheckInCheckOutAttachment {
                                    id
                                    attachmentName
                                    attachmentBase64
                                    attachmentType
                                    isCheckIn
                                    studentCheckInCheckOutId
                                    studentCheckInCheckOutPhysicalRecordId
                                }
                                studentOperatingHour {
                                    id
                                    operatingHour {
                                        name
                                        startTime
                                        endTime
                                        bufferTimeForOTCalc
                                        refOperatingType {
                                            name
                                        }
                                        fees {
                                            name
                                            amount
                                            isMandatory
                                            discountType {
                                                name
                                                value
                                                type
                                            }
                                            otCalculation {
                                                name
                                                type
                                                calculationType
                                                isOverTime
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        `;

        return this.apollo.watchQuery<any>({
            query: GET_STUDENTLIST
        }).valueChanges.pipe(
            map(result => {
                const studentListData = result?.data?.staffAssignee?.allStaffAssignee?.data || [];
                return studentListData;
            })
        );
    }


    createUser(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: []`;
                } else if (typeof value[0] === 'object') {
                    const formattedArray = value.map(obj => `{ ${Object.keys(obj).map(k => `${k}: "${obj[k]}"`).join(', ')} }`).join(', ');
                    return `${key}: [${formattedArray}]`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const CREATE_USER = gql`
            mutation {
                user {
                    addUser(input: { ${inputString} }){
                        success
                        error
                    }
                }
            }
        `;

        return this.apollo.mutate<any>({
            mutation: CREATE_USER
        }).pipe(
            map(result => {
                const user = result?.data?.user?.addUser;
                return user;
            })
        );
    }


    createCheckIn(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const CREATE_USER = gql`
            mutation {
                staffCheckInCheckOut {
                    addStaffCheckIn(input: { ${inputString} }){
                        success
                        error
                    }
                }
            }
        `;

        return this.apollo.mutate<any>({
            mutation: CREATE_USER
        }).pipe(
            map(result => {
                const user = result?.data?.staffCheckInCheckOut?.addStaffCheckIn;
                return user;
            })
        );
    }


    createCheckOut(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const CREATE_USER = gql`
            mutation {
                staffCheckInCheckOut {
                    addStaffCheckOut(input: { ${inputString} }){
                        success
                        error
                    }
                }
            }
        `;

        return this.apollo.mutate<any>({
            mutation: CREATE_USER
        }).pipe(
            map(result => {
                const user = result?.data?.staffCheckInCheckOut?.addStaffCheckOut;
                return user;
            })
        );
    }


    createTempStaffAssignee(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const CREATE_USER = gql`
            mutation {
                tempStaffAssignee {
                    addTempStaffAssignee(input: { ${inputString} }){
                        success
                        error
                    }
                }
            }
        `;

        return this.apollo.mutate<any>({
            mutation: CREATE_USER
        }).pipe(
            map(result => {
                const user = result?.data?.tempStaffAssignee?.addTempStaffAssignee;
                return user;
            })
        );
    }


    updateUser(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: []`;
                } else if (typeof value[0] === 'object') {
                    const formattedArray = value.map(obj => `{ ${Object.keys(obj).map(k => `${k}: "${obj[k]}"`).join(', ')} }`).join(', ');
                    return `${key}: [${formattedArray}]`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');
        
        const UPDATE_USER = gql`
            mutation {
                user {
                    updateUser(input: { ${inputString} }){
                        success
                        error
                    }
                }
            }
        `;

        return this.apollo.mutate<any>({
            mutation: UPDATE_USER
        }).pipe(
            map(result => {
                const user = result?.data?.user?.updateUser;
                return user;
            })
        );
    }


    updateCheckIn(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const UPDATE_USER = gql`
            mutation {
                staffCheckInCheckOut {
                    updateStaffCheckIn(input: { ${inputString} }){
                        success
                        error
                    }
                }
            }
        `;

        return this.apollo.mutate<any>({
            mutation: UPDATE_USER
        }).pipe(
            map(result => {
                const user = result?.data?.staffCheckInCheckOut?.updateStaffCheckIn;
                return user;
            })
        );
    }


    updateCheckOut(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const UPDATE_USER = gql`
            mutation {
                staffCheckInCheckOut {
                    updateStaffCheckOut(input: { ${inputString} }){
                        success
                        error
                    }
                }
            }
        `;

        return this.apollo.mutate<any>({
            mutation: UPDATE_USER
        }).pipe(
            map(result => {
                const user = result?.data?.staffCheckInCheckOut?.updateStaffCheckOut;
                return user;
            })
        );
    }


    updateTempStaffAssignee(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const UPDATE_USER = gql`
            mutation {
                tempStaffAssignee {
                    updateTempStaffAssignee(input: { ${inputString} }){
                        success
                        error
                    }
                }
            }
        `;

        return this.apollo.mutate<any>({
            mutation: UPDATE_USER
        }).pipe(
            map(result => {
                const user = result?.data?.tempStaffAssignee?.updateTempStaffAssignee;
                return user;
            })
        );
    }


    deleteUser(id: number): Observable<any> {
        const DELETE_USER = gql`
            mutation {
                user {
                    deleteUser(input:  {
                        id: ${id},
                    }){
                        success
                        error
                    }
                }
            }
        `;

        return this.apollo.mutate<any>({
            mutation: DELETE_USER,
        }).pipe(
            map(result => {
                const user = result?.data?.user?.deleteUser;
                return user;
            })
        );
    }


    deleteCheckInOut(id: number): Observable<any> {
        const DELETE_USER = gql`
            mutation {
                staffCheckInCheckOut {
                    deleteStaffCheckInCheckOut(input:  {
                        id: ${id},
                    }){
                        success
                        error
                    }
                }
            }
        `;

        return this.apollo.mutate<any>({
            mutation: DELETE_USER,
        }).pipe(
            map(result => {
                const user = result?.data?.staffCheckInCheckOut?.deleteStaffCheckInCheckOut;
                return user;
            })
        );
    }


    deleteTempStaffAssignee(id: number): Observable<any> {
        const DELETE_USER = gql`
            mutation {
                tempStaffAssignee {
                    deleteTempStaffAssignee(input:  {
                        id: ${id},
                    }){
                        success
                        error
                    }
                }
            }
        `;

        return this.apollo.mutate<any>({
            mutation: DELETE_USER,
        }).pipe(
            map(result => {
                const user = result?.data?.tempStaffAssignee?.deleteTempStaffAssignee;
                return user;
            })
        );
    }

    updateMasterStaffAssignee(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const UPDATE_MASTER_STAFF_ASSIGNEE = gql`
            mutation {
                masterStaffAssignee {
                    updateMasterStaffAssignee(input:  { ${inputString} }){
                        success
                        error
                    }
                }
            }
        `;

        return this.apollo.mutate<any>({
            mutation: UPDATE_MASTER_STAFF_ASSIGNEE
        }).pipe(
            map(result => {
                const response = result?.data?.masterStaffAssignee?.updateMasterStaffAssignee;
                return response;
            })
        );
    }


    getUserById(id: number): Observable<any> {
        const GET_USER = gql`
            query {
                user {
                    userById(id: ${id}) {
                        id
                        email
                        firstName
                        lastName
                        phoneNumber
                        profilePictureBase64
                        userRoles {
                            id
                            refRole {
                                id
                                name
                            }
                            school {
                                id
                                name
                            }
                        }
                        userAccess {
                            id
                            module
                            accessLevel
                        }
                    }
                }
            }
        `;

        return this.apollo.query<any>({
            query: GET_USER,
            variables: { id: id },
            fetchPolicy: 'network-only'
        }).pipe(
            map(result => {
                const user = result?.data?.user?.userById[0];
                return user;
            })
        );
    }

    getPasswordResetStatus(token: string): Observable<any> {
        const GET_PASSWORD_RESET_STATUS = gql`
            query ($token: String!) {
                passwordReset {
                    passwordResetStatus(token: $token) {
                        isValid
                        isFirstTime
                        error
                    }
                }
            }
        `;

        return this.apollo.query<any>({
            query: GET_PASSWORD_RESET_STATUS,
            variables: { token },
            fetchPolicy: 'no-cache'
        }).pipe(
            map(result => result?.data?.passwordReset?.passwordResetStatus)
        );
    }


    private removeTypename(data: any): any {
        if (Array.isArray(data)) {
            return data.map(item => this.removeTypename(item));
        } else if (data !== null && typeof data === 'object') {
            const newObj: any = {};
            Object.keys(data).forEach(key => {
                if (key !== '__typename') {
                    newObj[key] = this.removeTypename(data[key]);
                }
            });
            return newObj;
        }
        return data;
    }
}
